import { useCallback, useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import api, { formatApiErrorDetail } from "@/lib/api";

export const LIMIT = 50;
export const LAYOUT = "main products";

/**
 * Encapsulates the Dashboard's product list state: search/category/CBM/QTY filters, sort,
 * pagination, the fetched records themselves, and the product-drawer prev/next navigation that
 * depends on that record list. Extracted from Dashboard.jsx so the page component itself only
 * has to compose UI, not manage ~150 lines of this fetching/paging logic directly.
 */
export function useDashboardProducts() {
  const { toast } = useToast();

  const [fields, setFields] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [records, setRecords] = useState([]);
  const [foundCount, setFoundCount] = useState(0);
  const [offset, setOffset] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [cbmMinInput, setCbmMinInput] = useState("");
  const [cbmMaxInput, setCbmMaxInput] = useState("");
  const [qtyMinInput, setQtyMinInput] = useState("");
  const [qtyMaxInput, setQtyMaxInput] = useState("");
  const [dateFromInput, setDateFromInput] = useState("");
  const [dateToInput, setDateToInput] = useState("");
  const [cbmMin, setCbmMin] = useState(null);
  const [cbmMax, setCbmMax] = useState(null);
  const [qtyMin, setQtyMin] = useState(null);
  const [qtyMax, setQtyMax] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [view, setView] = useState("inventory");
  const [showAll, setShowAll] = useState(false);
  const [bestOnly, setBestOnly] = useState(false);
  const [blockedOnly, setBlockedOnly] = useState(false);
  const [tag, setTag] = useState("");
  const [sortField, setSortField] = useState("CBM");
  const [sortOrder, setSortOrder] = useState("descend"); // largest CBM first by default
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [pendingNav, setPendingNav] = useState(null);

  useEffect(() => {
    api
      .get(`/fm/layout-fields?layout=${encodeURIComponent(LAYOUT)}`)
      .then(({ data }) => {
        setFields(data?.fields || []);
      })
      .catch(() => {
        // FileMaker may be unreachable; Dashboard still works from the MongoDB
        // product cache. Fall back to an empty field list so filters degrade
        // gracefully instead of crashing the page.
        setFields([]);
      });
    api
      .get("/fm/categories")
      .then(({ data }) => {
        setCategoryOptions(data.categories || []);
      })
      .catch((e) => {
        toast({
          title: "Category filter unavailable",
          description: formatApiErrorDetail(e.response?.data?.detail),
          variant: "destructive",
        });
      });
  }, [toast]);

  // Sub-categories depend on the selected Category so buyers only see sub-cats
  // that actually exist inside that group. Runs on mount (empty category ->
  // all sub-cats) and every time the buyer changes Category.
  useEffect(() => {
    const params = {};
    if (category) params.category = category;
    api
      .get("/fm/sub-categories", { params })
      .then(({ data }) => setSubCategoryOptions(data.sub_categories || []))
      .catch(() => setSubCategoryOptions([]));
    // Whenever category changes, clear any stale sub-category selection so the
    // fetchRecords call below doesn't send a sub_category that no longer
    // belongs to the newly-picked category.
    setSubCategory("");
  }, [category]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = { layout: LAYOUT, offset, limit: LIMIT };
      if (search) params.q = search;
      if (category) params.category = category;
      if (subCategory) params.sub_category = subCategory;
      if (view !== "best_selling") {
        if (cbmMin !== null && cbmMin !== "") params.cbm_min = cbmMin;
        if (cbmMax !== null && cbmMax !== "") params.cbm_max = cbmMax;
        if (qtyMin !== null && qtyMin !== "") params.qty_min = qtyMin;
        if (qtyMax !== null && qtyMax !== "") params.qty_max = qtyMax;
      }
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (view === "best_selling") params.best_selling = 1;
      if (view !== "best_selling" && bestOnly) params.best_selling = 1;
      if (view !== "best_selling" && showAll) params.show_all = 1;
      if (view !== "best_selling" && blockedOnly) params.blocked = 1;
      if (view !== "best_selling" && tag) params.tag = tag;
      if (sortField && sortOrder) {
        params.sort_field = sortField;
        params.sort_order = sortOrder;
      }
      const { data } = await api.get("/fm/records", { params });
      setRecords(data.response?.data || []);
      setFoundCount(data.response?.dataInfo?.foundCount || 0);
    } catch (e) {
      toast({
        title: "Failed to load records",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [offset, search, category, subCategory, cbmMin, cbmMax, qtyMin, qtyMax, dateFrom, dateTo, view, showAll, bestOnly, blockedOnly, tag, sortField, sortOrder, toast]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  useEffect(() => {
    if (pendingNav && records.length > 0) {
      const idx = pendingNav === "first" ? 0 : records.length - 1;
      setSelectedRecord(records[idx]);
      setSelectedIndex(idx);
      setPendingNav(null);
    }
  }, [records, pendingNav]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setOffset(1);
    setSearch(searchInput);
    setCbmMin(cbmMinInput.trim() === "" ? null : parseFloat(cbmMinInput));
    setCbmMax(cbmMaxInput.trim() === "" ? null : parseFloat(cbmMaxInput));
    setQtyMin(qtyMinInput.trim() === "" ? null : parseFloat(qtyMinInput));
    setQtyMax(qtyMaxInput.trim() === "" ? null : parseFloat(qtyMaxInput));
    setDateFrom(dateFromInput);
    setDateTo(dateToInput);
  };

  const handleCategoryChange = (val) => {
    setCategory(val === "all" ? "" : val);
    setOffset(1);
  };

  const handleSubCategoryChange = (val) => {
    setSubCategory(val === "all" ? "" : val);
    setOffset(1);
  };

  const handleViewChange = (val) => {
    setView(val);
    setOffset(1);
  };

  const handleSortFieldChange = (val) => {
    setSortField(val);
    setOffset(1);
  };

  const toggleSortOrder = () => {
    setOffset(1);
    setSortOrder((prev) => (prev === "ascend" ? "descend" : "ascend"));
  };

  const openRecord = (rec, idx) => {
    setSelectedRecord(rec);
    setSelectedIndex(idx ?? null);
  };

  const openCreate = () => {
    setSelectedRecord(null);
    setSelectedIndex(null);
  };

  const handleNavigate = (direction) => {
    if (direction === "next") {
      if (selectedIndex !== null && selectedIndex < records.length - 1) {
        const idx = selectedIndex + 1;
        setSelectedRecord(records[idx]);
        setSelectedIndex(idx);
      } else if (offset + LIMIT <= foundCount) {
        setPendingNav("first");
        setOffset(offset + LIMIT);
      }
    } else {
      if (selectedIndex !== null && selectedIndex > 0) {
        const idx = selectedIndex - 1;
        setSelectedRecord(records[idx]);
        setSelectedIndex(idx);
      } else if (offset > 1) {
        setPendingNav("last");
        setOffset(Math.max(1, offset - LIMIT));
      }
    }
  };

  const canGoPrev = selectedIndex !== null && (selectedIndex > 0 || offset > 1);
  const canGoNext = selectedIndex !== null && (selectedIndex < records.length - 1 || offset + LIMIT <= foundCount);
  const totalPages = Math.max(1, Math.ceil(foundCount / LIMIT));
  const currentPage = Math.floor((offset - 1) / LIMIT) + 1;

  return {
    fields,
    categoryOptions,
    subCategoryOptions,
    records,
    foundCount,
    offset,
    setOffset,
    search,
    searchInput,
    setSearchInput,
    cbmMinInput,
    setCbmMinInput,
    cbmMaxInput,
    setCbmMaxInput,
    qtyMinInput,
    setQtyMinInput,
    qtyMaxInput,
    setQtyMaxInput,
    dateFromInput,
    setDateFromInput,
    dateToInput,
    setDateToInput,
    cbmMin,
    cbmMax,
    qtyMin,
    qtyMax,
    dateFrom,
    dateTo,
    category,
    subCategory,
    view,
    showAll,
    tag,
    handleTagChange: (val) => {
      setTag(val === "all" ? "" : val);
      setOffset(1);
    },
    toggleShowAll: () => {
      setOffset(1);
      setShowAll((v) => !v);
    },
    bestOnly,
    toggleBestOnly: () => {
      setOffset(1);
      setBestOnly((v) => !v);
    },
    blockedOnly,
    toggleBlockedOnly: () => {
      setOffset(1);
      setBlockedOnly((v) => !v);
    },
    sortField,
    sortOrder,
    loading,
    selectedRecord,
    selectedIndex,
    fetchRecords,
    handleSearchSubmit,
    handleCategoryChange,
    handleSubCategoryChange,
    handleViewChange,
    handleSortFieldChange,
    toggleSortOrder,
    openRecord,
    handleNavigate,
    canGoPrev,
    canGoNext,
    totalPages,
    currentPage,
  };
}
